import React, { useState } from "react";
import { Link2, CheckCircle2, Clipboard, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const RequestProduct = () => {
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error("Please paste the product link first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/product-requests", {
        product_url: url.trim(), note: note.trim(), name: name.trim(), email: email.trim(), company,
      });
      setDone(true);
      toast.success(data.message || "Request sent!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) { setUrl(text.trim()); toast.success("Pasted from clipboard"); }
    } catch {
      toast.info("Long-press the box and tap Paste to add your link.");
    }
  };

  return (
    <section data-testid="request-product" className="rounded-2xl bg-navy overflow-hidden">
      <div className="relative px-6 py-10 sm:px-10 sm:py-12">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, #f2b134 0, transparent 40%), radial-gradient(circle at 85% 10%, #b5231d 0, transparent 35%)" }} />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-gold text-xs font-bold uppercase tracking-[0.2em]">
            <Link2 className="w-4 h-4" /> Request a Product
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl text-cream leading-tight">
            Can't find it? <span className="text-gold">Send me the link.</span>
          </h2>
          <p className="mt-3 text-cream/75 leading-relaxed">
            Found something you love on Amazon or anywhere else? Just paste the product link below.
            I'll add it to the store — usually <span className="text-cream font-bold">within 48 hours</span>.
          </p>

          {/* Simple 3-step instructions */}
          <ol className="mt-5 space-y-2 text-cream/80 text-sm">
            <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-brick text-white font-bold text-xs flex items-center justify-center">1</span> Copy the product link (from Amazon or any store).</li>
            <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-brick text-white font-bold text-xs flex items-center justify-center">2</span> Paste it in the box below.</li>
            <li className="flex items-start gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-brick text-white font-bold text-xs flex items-center justify-center">3</span> Tap send — I'll add it to the store within 48 hours.</li>
          </ol>

          {done ? (
            <div data-testid="request-product-success" className="mt-7 rounded-xl bg-cream p-6 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <p className="mt-3 font-display text-2xl text-navy">Request received!</p>
              <p className="mt-1 text-ink/70">Thanks — I'll review your link and add it to the store within 48 hours.</p>
              <button
                data-testid="request-product-again"
                onClick={() => { setDone(false); setUrl(""); setNote(""); }}
                className="mt-4 inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors"
              >
                Request another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-3">
              {/* honeypot (hidden from real users) */}
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

              <div className="relative">
                <input
                  data-testid="request-product-url"
                  type="text"
                  inputMode="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste the product link here…"
                  className="w-full rounded-xl bg-cream text-navy placeholder:text-navy/45 pl-4 pr-28 py-4 text-base font-medium outline-none focus:ring-2 focus:ring-gold"
                />
                <button
                  type="button"
                  data-testid="request-product-paste"
                  onClick={pasteFromClipboard}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-lg bg-navy/10 text-navy font-bold text-sm px-3 py-2 hover:bg-navy/20 transition-colors"
                >
                  <Clipboard className="w-4 h-4" /> Paste
                </button>
              </div>

              <input
                data-testid="request-product-note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything else? (size, color, quantity) — optional"
                className="w-full rounded-xl bg-cream/95 text-navy placeholder:text-navy/45 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  data-testid="request-product-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full rounded-xl bg-cream/95 text-navy placeholder:text-navy/45 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold"
                />
                <input
                  data-testid="request-product-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email if you'd like an update (optional)"
                  className="w-full rounded-xl bg-cream/95 text-navy placeholder:text-navy/45 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold"
                />
              </div>

              <button
                data-testid="request-product-submit"
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase rounded-xl px-8 py-4 hover:bg-gold hover:text-navy transition-colors disabled:opacity-60"
              >
                <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send my request"}
              </button>
              <p className="text-cream/50 text-xs">No account or email needed — just the link. I'll take it from there.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
